<template>
  <div class="mytabs history-page">
    <div class="sidebar-container">
      <SideBar
        :name="user?.name"
        :email="user?.email"
      />
    </div>

    <div class="history-container">
      <div class="history-header mt-4 mb-3">
        <BaseButton
          bare
          variant=""
          class="btn-back"
          @click="$router.push('/home')"
        >
          <font-awesome-icon icon="arrow-left" />
          {{ $t('history.back') }}
        </BaseButton>
        <div
          class="title text-center mt-3"
          v-text="$t('history.rent')"
        ></div>
        <div
          class="subtitle text-center"
          v-text="$t('history.subtitle')"
        ></div>
      </div>

      <div class="text-center">
        <BSpinner
          v-if="spinner"
          id="loading-spinner"
        />
      </div>
      <!-- Moved summary below -->
      <div
        v-if="!spinner && results.length === 0"
        class="empty-state-card"
      >
        <font-awesome-icon
          icon="fa-solid fa-receipt"
          class="empty-state-icon"
        />
        <div
          class="empty-state-title"
          v-text="$t('history.no_transactions')"
        ></div>
        <div
          class="empty-state-subtitle"
          v-text="$t('history.empty_hint')"
        ></div>
      </div>
      <div
        v-for="(result, index) in results"
        :key="result.id ? `history-${result.id}-${index}` : `history-index-${index}`"
        class="select-card subscribe-margin history-card"
      >
        <div class="history-card__header">
          <div class="history-card__title">
            <span
              class="history-card__label"
              v-text="$t('history.transaction')"
            ></span>
            <span class="history-card__id">#{{ result.id }}</span>
          </div>
          <div class="history-card__badges">
            <!-- Main status badge -->
            <StatusBadge
              :label="result.return_time === 0 ? $t('history.status_pending') : $t('history.status_completed')"
              :variant="result.return_time === 0 ? 'warning' : 'success'"
              :icon="result.return_time === 0 ? 'clock' : 'check-circle'"
            />

            <!-- Refund badge if applicable -->
            <StatusBadge
              v-if="isRefunded(result)"
              :label="$t('history.refunded_badge')"
              variant="info"
              icon="rotate-left"
            />
          </div>
        </div>
        <TransactionSessionDetails
          :result="result"
          :show-transaction-rating="true"
          class="history-card__details"
        />
      </div>
    </div>
    <div class="text-center history-load-more">
      <BaseButton
        v-if="next_page && results.length > 0 && page < totalPages"
        variant="primary"
        unstyled
        @click="get_history"
      >
        <div v-text="$t('history.load_more')"></div>
      </BaseButton>
    </div>
  </div>
</template>

<script>
  import API from '@/services/api'
  import handleError from '@/services/handleError'
  import { sessionRecorder, UserEvent } from '@/services/sessionRecorder'
  import getUser from '@/views/home/service/getUser.js'

  const GTAG_HISTORY = 'history'
  import TransactionSessionDetails from '@/components/TransactionSessionDetails.vue'
  import BaseButton from '@/components/ui/BaseButton.vue'
  import SideBar from '@/components/SideBar.vue'
  import StatusBadge from '@/components/ui/StatusBadge.vue'
  import { isTransactionRefunded } from '@/constants/status-constants'
  import { BSpinner } from 'bootstrap-vue-next'

  export default {
    name: 'RentalHistory',
    components: {
      TransactionSessionDetails,
      BaseButton,
      SideBar,
      StatusBadge,
      BSpinner
    },
    props: [],
    data() {
      return {
        results: [],
        next_page: null,
        spinner: true,
        loadMoreSpinner: false,
        page: 1,
        totalPages: 1,
        total: 0,
        user: null
      }
    },
    mounted: async function () {
      let c = sessionStorage.getItem('campaign')
      if (c) {
        this.campaign = c
      }
      sessionRecorder.addUserEvent(new UserEvent(GTAG_HISTORY))

      try {
        this.user = await getUser()
      } catch (error) {
        console.error('Error fetching user:', error)
      }

      await this.get_history()
    },
    methods: {
      isRefunded(result) {
        return isTransactionRefunded(result)
      },

      formatDate: function (date) {
        return (
          [
            String(date.getDate()).padStart(2, '0'),
            date.toLocaleString('default', { month: 'short' }),
            date.getFullYear()
          ].join(' de ') +
          ' ' +
          [String(date.getHours()).padStart(2, '0'), String(date.getMinutes()).padStart(2, '0')].join(':')
        )
      },
      loadMore: async function () {
        this.loadMoreSpinner = true
        await this.get_history()
      },
      normalizeNextPage: function (nextPage) {
        if (!nextPage || typeof nextPage !== 'string') return null
        if (/^https?:\/\//i.test(nextPage)) return nextPage

        let fixed = nextPage

        fixed = fixed.replace(/(0\.0\.0\.0)(transaction)/, '$1/$2')

        if (!fixed.startsWith('/')) {
          fixed = '/' + fixed
        }

        return fixed
      },

      get_history: async function () {
        let url = 'transaction/history?items_per_page=20'
        if (this.results.length > 0 && this.next_page) {
          url = this.normalizeNextPage(this.next_page)
        }

        try {
          const response = await API.get(url)

          const data = response.data
          if (!data) {
            this.spinner = false
            return
          }

          const meta = data.meta || {}
          this.next_page = meta.next_page
          this.page = meta.page || 1
          this.totalPages = meta.total_pages || 1
          this.total = meta.count || 0

          // Robustly handle different API response structures by collecting from all likely fields
          let results_temp = []
          if (Array.isArray(data.results)) {
            results_temp = [...data.results]
          } else if (Array.isArray(data)) {
            results_temp = [...data]
          }

          if (results_temp && results_temp.length > 0) {
            let processed = this.modify_api_response(results_temp)

            if (this.results.length === 0) {
              this.results = processed
            } else {
              this.results.push(...processed)
            }
          }
          this.spinner = false
          this.loadMoreSpinner = false
        } catch (error) {
          handleError(error)
          this.spinner = false
          this.loadMoreSpinner = false
        }
      },
      modify_api_response: function (arr) {
        return arr.map(element => ({
          ...element,
          take_date: this.formatDate(new Date(element.take_time * 1000)),
          no_plan: element.plan_name === '',
          elapsed_time:
            element.return_time === 0
              ? Math.floor(Date.now() / 1000) - element.take_time
              : element.return_time - element.take_time,
          return_date: element.return_time === 0 ? undefined : this.formatDate(new Date(element.return_time * 1000))
        }))
      }
    }
  }
</script>

<style scoped lang="scss">
  @use '@/main';

  .history-container {
    max-width: 600px;
    margin: 0 auto;
    padding: 0 15px;
  }

  .history-header {
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .btn-back {
    color: #6c757d;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.5rem 1rem;
    border-radius: 50px;
    background: white;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 8px;

    &:hover {
      color: main.$color-energy-orange;
      border-color: main.$color-energy-orange;
      box-shadow: 0 2px 8px rgba(255, 107, 0, 0.12);
    }
  }

  .title {
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0;
    width: 100%;
  }

  .subtitle {
    font-size: 15px;
    color: #6b7280;
    width: 100%;
  }

  #loading-spinner {
    color: main.$color-energy-orange !important;
  }

  .mytabs {
    overflow: auto;
  }

  .history-page {
    margin-top: calc(var(--app-fixed-top-offset, 0px) + 80px);
    min-height: 100%;
    background: #f6f7f9;
    padding-bottom: calc(var(--app-banner-bottom-height, 0px) + 24px);
  }

  .sidebar-container {
    position: fixed;
    top: var(--app-fixed-top-offset, 0px);
    left: 0;
    right: 0;
    width: 100%;
    padding: 10px;
    box-sizing: border-box;
    z-index: 1600;
  }

  .subscribe-margin {
    margin-bottom: 15px !important;
  }

  .history-card {
    background-color: #fff !important;
    padding: 16px !important;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
  }

  .history-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .history-card__title {
    display: flex;
    align-items: baseline;
    gap: 6px;
    color: #111827;
  }

  .history-card__label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
  }

  .history-card__id {
    font-size: 16px;
    font-weight: 600;
  }

  .history-card__badges {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 5px;
    flex-shrink: 0;
    max-width: 60%;
  }

  .history-load-more {
    margin: 12px 0 24px;
  }

  .history-load-more-spinner {
    margin: 12px 0 24px;
    display: block;
  }

  .empty-state-card {
    padding: 30px 15px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin: 20px 0;
    text-align: center;
  }

  .empty-state-icon {
    font-size: 28px;
    color: main.$color-energy-orange;
    margin-bottom: 10px;
  }

  .empty-state-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }

  .empty-state-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin-top: 4px;
  }
</style>
